import { createFileRoute, Link } from '@tanstack/react-router';
import { Alert, Grid, Group, Stack, Text } from '@mantine/core';
import { AlertTriangle, BookOpen, GraduationCap, Library, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { Badge, Card, EmptyState, PageContainer, PageHeader, Skeleton } from '../components/ui';
import { staggerContainer, cardItem } from '../lib/motion';
import { useCourses } from '../hooks/useCourses';
import { selectRole, useAuthStore } from '../store/useAuthStore';
import type { CourseSummary, Difficulty } from '../api/types';

const DIFFICULTY_VARIANT: Record<Difficulty, 'success' | 'info' | 'warn' | 'danger'> = {
  beginner: 'success',
  intermediate: 'info',
  advanced: 'warn',
  expert: 'danger',
};

export const Route = createFileRoute('/courses/')({
  component: CoursesIndex,
});

function CoursesIndex() {
  const isTrainee = useAuthStore(selectRole) === 'trainee';
  const { data: raw, isPending, isError, error } = useCourses();
  // Trainees see only courses they're enrolled in — never the full catalog.
  const data = isTrainee ? raw?.filter((c) => c.enrolled) : raw;

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={GraduationCap}
        title="Courses"
        description="Guided learning paths that combine labs, scenarios and assessments."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Courses' }]}
      />

      {isError ? (
        <Alert color="red" icon={<AlertTriangle size={16} />} variant="light" title="Load failed">
          {(error as Error)?.message ?? 'Unknown error'}
        </Alert>
      ) : isPending ? (
        <Grid gutter="md">
          {Array.from({ length: 3 }).map((_, i) => (
            <Grid.Col key={i} span={{ base: 12, md: 4 }}>
              <Skeleton height={180} radius="md" />
            </Grid.Col>
          ))}
        </Grid>
      ) : data && data.length === 0 ? (
        <EmptyState
          illustration={<Library size={48} />}
          title="No courses available"
          description="Your org admin can publish courses for your tenant. Once published they show up here."
        />
      ) : (
        <motion.div variants={staggerContainer} initial="hidden" animate="show">
          <Grid gutter="md">
            {data?.map((course) => <CourseTile key={course.id} course={course} />)}
          </Grid>
        </motion.div>
      )}
    </PageContainer>
  );
}

function CourseTile({ course }: { course: CourseSummary }) {
  return (
    <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
      <motion.div variants={cardItem} style={{ height: '100%' }}>
        <Link
          to="/courses/$id"
          params={{ id: course.slug }}
          preload="intent"
          style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}
        >
          <Card hoverable style={{ height: '100%' }}>
            <Stack gap="sm" justify="space-between" style={{ height: '100%' }}>
              <Stack gap="xs">
                <Group justify="space-between" align="start">
                  <Text fw={700} size="md">
                    {course.title}
                  </Text>
                  <Badge variant={DIFFICULTY_VARIANT[course.difficulty]} size="xs">
                    {course.difficulty}
                  </Badge>
                </Group>
                <Text size="xs" c="dimmed" lineClamp={3}>
                  {course.description}
                </Text>
              </Stack>
              <Group gap="lg">
                <CourseMetric icon={<BookOpen size={12} />} label={`${course.moduleCount} modules`} />
                <CourseMetric icon={<Users size={12} />} label={`${course.enrolledCount} enrolled`} />
                <CourseMetric icon={<Library size={12} />} label={`${course.estimatedHours}h`} />
              </Group>
            </Stack>
          </Card>
        </Link>
      </motion.div>
    </Grid.Col>
  );
}

function CourseMetric({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <Group gap={4} wrap="nowrap">
      <span style={{ color: '#71717a' }}>{icon}</span>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Group>
  );
}
